import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronLeft, RefreshCw, Search, Save, Plus, Trash2, ExternalLink, Upload, Image as ImageIcon } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { clearSeoCache } from "../components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => {
  const token = localStorage.getItem("lf_session");
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
};

const EMPTY = {
  path: "",
  label: "",
  title: "",
  description: "",
  keywords: "",
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  og_type: "website",
  twitter_card: "summary_large_image",
  noindex: false,
};

export default function AdminSEO() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosAuth().get("/admin/seo-pages");
      setPages(data.pages || []);
      if (!selected && data.pages?.length) {
        setSelected(data.pages[0].path);
        setForm({ ...EMPTY, ...data.pages[0] });
      }
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load SEO pages");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = pages.filter((p) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (p.path + " " + (p.label || "") + " " + (p.title || "")).toLowerCase().includes(q);
  });

  const select = (p) => {
    setSelected(p.path);
    setForm({ ...EMPTY, ...p });
  };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.path.trim()) { toast.error("Path is required"); return; }
    setSaving(true);
    try {
      const path = form.path.trim().startsWith("/") ? form.path.trim() : "/" + form.path.trim();
      const { data } = await axiosAuth().put(`/admin/seo-pages${path}`, { ...form, path });
      clearSeoCache(path);
      toast.success(`Saved ${path}`);
      setSelected(path);
      setForm({ ...EMPTY, ...data });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete SEO entry for ${selected}?`)) return;
    try {
      await axiosAuth().delete(`/admin/seo-pages${selected}`);
      clearSeoCache(selected);
      toast.success("Deleted");
      setSelected(null);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const newPage = () => {
    setSelected(null);
    setForm({ ...EMPTY, path: "/" });
  };

  const reseed = async () => {
    try {
      const { data } = await axiosAuth().post("/admin/seo-pages/reseed-defaults");
      toast.success(`Re-seeded ${data.inserted} default page(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reseed failed");
    }
  };

  const onOgImagePicked = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = ""; // so selecting the same file twice re-fires
    if (!f) return;
    const MAX = 5 * 1024 * 1024;
    if (f.size > MAX) { toast.error("Image is larger than 5 MB"); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axiosAuth().post("/admin/seo/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange("og_image", data.url);
      toast.success("Image uploaded — remember to hit Save");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-50 flex items-center justify-center"><Loader2 size={28} className="animate-spin text-zinc-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-seo-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/dashboard")} className="p-1.5 rounded-lg hover:bg-zinc-100" data-testid="btn-seo-back" aria-label="Back"><ChevronLeft size={18} /></button>
          <BrandLogo variant="mark" size={32} />
          <div>
            <div className="font-bold text-zinc-900">SEO & Page Meta</div>
            <div className="text-xs text-zinc-500">Title, description, keywords, Open Graph image — per page.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="btn-seo-reseed" onClick={reseed} className="flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 border border-zinc-200 px-3 py-2 rounded-lg hover:bg-zinc-50"><RefreshCw size={14} /> Re-seed defaults</button>
          <button data-testid="btn-seo-new" onClick={newPage} className="flex items-center gap-2 text-sm text-white bg-zinc-950 px-3 py-2 rounded-lg hover:bg-zinc-800"><Plus size={14} /> New page</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
        {/* Sidebar list */}
        <aside className="bg-white border border-zinc-200 rounded-2xl p-3 h-max" data-testid="seo-page-list">
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="input-seo-filter"
              placeholder="Filter pages…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900"
            />
          </div>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto">
            {filtered.map((p) => (
              <button
                key={p.path}
                data-testid={`seo-page-item-${p.path.replace(/\//g, "_") || "root"}`}
                onClick={() => select(p)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selected === p.path ? "bg-zinc-950 text-white" : "hover:bg-zinc-100 text-zinc-700"}`}
              >
                <div className="font-medium truncate">{p.label || p.path}</div>
                <div className={`text-xs font-mono truncate ${selected === p.path ? "text-zinc-300" : "text-zinc-500"}`}>{p.path}</div>
              </button>
            ))}
            {filtered.length === 0 && <div className="text-xs text-zinc-400 italic px-3 py-4">No pages</div>}
          </div>
        </aside>

        {/* Editor */}
        <section className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-5" data-testid="seo-editor">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-zinc-900">{selected ? "Edit page meta" : "New page"}</h2>
              <p className="text-xs text-zinc-500">{selected ? `Editing ${selected}` : "Define a new path-specific SEO block"}</p>
            </div>
            <div className="flex items-center gap-2">
              {selected && (
                <a
                  href={selected}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="btn-seo-open-page"
                  className="text-xs px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1"
                >
                  <ExternalLink size={12} /> Open page
                </a>
              )}
              {selected && (
                <button data-testid="btn-seo-delete" onClick={remove} className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Path" hint="Pathname only — must start with '/'." required>
              <input data-testid="input-seo-path" value={form.path} onChange={(e) => onChange("path", e.target.value)} placeholder="/about" className="lf-seo-input font-mono" />
            </Field>
            <Field label="Internal label" hint="Just for the admin list — not used on the public page.">
              <input data-testid="input-seo-label" value={form.label} onChange={(e) => onChange("label", e.target.value)} placeholder="About" className="lf-seo-input" />
            </Field>
          </div>

          <Field label="Title (≤ 60 chars recommended)" hint="Shows in browser tabs and search results.">
            <input data-testid="input-seo-title" value={form.title} onChange={(e) => onChange("title", e.target.value)} maxLength={120} className="lf-seo-input" />
            <Counter value={form.title} max={60} />
          </Field>
          <Field label="Meta description (≤ 160 chars recommended)" hint="The snippet Google shows under the title.">
            <textarea data-testid="input-seo-description" value={form.description} onChange={(e) => onChange("description", e.target.value)} maxLength={300} rows={3} className="lf-seo-input" />
            <Counter value={form.description} max={160} />
          </Field>
          <Field label="Keywords (comma-separated)" hint="Used by some niche search engines + internal site search.">
            <input data-testid="input-seo-keywords" value={form.keywords} onChange={(e) => onChange("keywords", e.target.value)} placeholder="MVP, prototype, AI development" className="lf-seo-input" />
          </Field>
          <Field label="Canonical URL (optional)" hint="Absolute URL — overrides the default same-page canonical.">
            <input data-testid="input-seo-canonical" value={form.canonical_url} onChange={(e) => onChange("canonical_url", e.target.value)} placeholder="https://fastprototype.ai/about" className="lf-seo-input" />
          </Field>

          <div className="border-t border-zinc-100 pt-5">
            <h3 className="font-semibold text-zinc-900 mb-3">Open Graph & Twitter</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="OG title">
                <input data-testid="input-seo-og-title" value={form.og_title} onChange={(e) => onChange("og_title", e.target.value)} className="lf-seo-input" />
              </Field>
              <Field label="OG type">
                <select data-testid="input-seo-og-type" value={form.og_type} onChange={(e) => onChange("og_type", e.target.value)} className="lf-seo-input">
                  <option>website</option>
                  <option>article</option>
                  <option>product</option>
                  <option>profile</option>
                </select>
              </Field>
            </div>
            <Field label="OG description">
              <textarea data-testid="input-seo-og-description" value={form.og_description} onChange={(e) => onChange("og_description", e.target.value)} rows={2} className="lf-seo-input" />
            </Field>
            <Field label="OG image" hint="Upload a 1200×630 PNG/JPG or paste a public URL. Used by Facebook, Twitter, LinkedIn, Slack…">
              <div className="flex items-stretch gap-2">
                <input
                  data-testid="input-seo-og-image"
                  value={form.og_image}
                  onChange={(e) => onChange("og_image", e.target.value)}
                  placeholder="/api/uploads/seo/og-home.png"
                  className="lf-seo-input flex-1"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={onOgImagePicked}
                  className="hidden"
                  data-testid="input-seo-og-file"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="btn-seo-og-upload"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 border border-zinc-200 bg-white rounded-lg text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                >
                  {uploadingImage ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {uploadingImage ? "Uploading…" : "Upload"}
                </button>
                {form.og_image && (
                  <button
                    type="button"
                    onClick={() => onChange("og_image", "")}
                    data-testid="btn-seo-og-clear"
                    title="Remove image"
                    className="inline-flex items-center gap-1 px-2.5 py-2 border border-zinc-200 bg-white rounded-lg text-xs text-zinc-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
              {form.og_image ? (
                <img
                  data-testid="seo-og-preview"
                  src={form.og_image}
                  alt="OG preview"
                  className="mt-3 max-h-40 rounded-lg border border-zinc-200 bg-zinc-50"
                  onError={(e) => (e.target.style.display = "none")}
                />
              ) : (
                <div className="mt-3 flex items-center gap-2 text-[11px] text-zinc-400 border border-dashed border-zinc-200 rounded-lg px-3 py-4">
                  <ImageIcon size={14} /> No image set — upload one above or paste a URL.
                </div>
              )}
            </Field>
            <Field label="Twitter card">
              <select data-testid="input-seo-twitter-card" value={form.twitter_card} onChange={(e) => onChange("twitter_card", e.target.value)} className="lf-seo-input">
                <option>summary_large_image</option>
                <option>summary</option>
              </select>
            </Field>
          </div>

          <div className="border-t border-zinc-100 pt-5 flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" data-testid="input-seo-noindex" checked={form.noindex} onChange={(e) => onChange("noindex", e.target.checked)} className="h-4 w-4 rounded border-zinc-300" />
              Hide from search engines (noindex, nofollow)
            </label>
            <button data-testid="btn-seo-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-zinc-950 text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-zinc-800 disabled:opacity-50">
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </section>
      </div>

      <style>{`
        .lf-seo-input {
          width: 100%;
          padding: 8px 12px;
          background: #fafafa;
          border: 1px solid #e4e4e7;
          border-radius: 10px;
          font-size: 13px;
          color: #18181b;
          outline: none;
        }
        .lf-seo-input:focus {
          background: #ffffff;
          border-color: #18181b;
          box-shadow: 0 0 0 3px rgba(24,24,27,0.06);
        }
      `}</style>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-zinc-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <div className="text-[11px] text-zinc-500">{hint}</div>}
    </div>
  );
}

function Counter({ value, max }) {
  const len = (value || "").length;
  const over = len > max;
  return (
    <div className={`text-[11px] text-right ${over ? "text-red-600 font-semibold" : "text-zinc-400"}`}>
      {len}/{max}
    </div>
  );
}
